import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import { type PostalCodeLocation } from '../services/state/model';
import services from '../services';
import { addPersistingQueryParamsToPath, getPath } from '../helpers/urlHelper';
import AppFooter from '../components/AppFooter';
import LoginLink from '../components/LoginLink';
import PageContent from '../components/PageContent';
import { shouldHideLoginLink } from '../helpers/loginLinkHelper';
import PageTitle from '../components/PageTitle';
import ZipCode from '../components/ZipCode';
import { useStateSelector, useStateDispatch } from '../store/';
import StickyFooter from '../components/StickyFooter';
import Button from '@ingka/button';
import { StyledWrapper } from '../components/PageLayout';
import { isZipCodeValid } from '../hooks/useZipCode';
import { selectAppOperation } from '../reducers/selectors';
import { saveUpdatedSessionItem } from '../helpers/sessionHelper';
import { ViewContext } from '../context';
import { updateCountryStateInSearch } from '../reducers/search/searchReducer';
import { isFeatureEnabled } from '../helpers/featureHelper';
import InlineMessage from '@ingka/inline-message';
import infoCircle from '@ingka/ssr-icon/paths/information-circle';

/* globals CSS_CONFIG */

const StyledInlineMessage = styled(InlineMessage)`
  margin: 1.5rem 0;
`;

const ContentWrapper = styled.div`
  min-height: 14rem;
`;

const StyledText = styled(Text)`
  &&& {
    margin-bottom: 1rem;
  }
`;

const { loginRequired } = CSS_CONFIG.services;
const { apiSrc } = CSS_CONFIG.getStates;
const { useInfoMessage } = CSS_CONFIG.configurableTranslationKeys.start;

const StartPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useStateDispatch();
  const { t } = useTranslation();
  const appOperation = useStateSelector(selectAppOperation);
  const view = useContext(ViewContext);
  const searchPostCode = useStateSelector((state) => state.search.postCode);
  const userPostCode = useStateSelector((state) => state.user.postCode);
  const isLoggedIn = useStateSelector((state) => state.user.isLoggedIn);
  const allowedMemberDiscounts = useStateSelector(
    (state) => state.user.allowedMemberDiscounts,
  );
  const customerType = useStateSelector((state) => state.user.customerType);
  const [isValidPostalCode, setIsValidPostalCode] = useState(true);
  const [postalCodeLocations, setPostalCodeLocations] = useState<
    PostalCodeLocation[]
  >([]);
  const [isLoadingPostalCodeLocations, setIsLoadingPostalCodeLocations] =
    useState(false);
  const isViewAlt = view === 'alt';
  const isViewApp = view === 'app';
  const zipCodeRef = useRef<HTMLInputElement>(null);

  const showLoginLink =
    !isViewApp &&
    !isViewAlt &&
    !shouldHideLoginLink(
      isLoggedIn,
      allowedMemberDiscounts,
      customerType,
      false,
      undefined,
    );

  const updatePostalCodeLocations = async (): Promise<{
    navigateToServices: boolean;
    locations: PostalCodeLocation[];
  }> => {
    setIsLoadingPostalCodeLocations(true);
    const postalCodeLookup =
      await services.state.getPostalCodeLocations(searchPostCode);
    setPostalCodeLocations(postalCodeLookup.locations);
    setIsLoadingPostalCodeLocations(false);

    if (!postalCodeLookup.valid) {
      setIsValidPostalCode(false);
      return { navigateToServices: false, locations: [] };
    }

    return {
      navigateToServices: postalCodeLookup.locations.length < 2,
      locations: postalCodeLookup.locations,
    };
  };

  const shouldUpdatePostalCodeLocations = (): boolean => {
    const hasApiConfigured = apiSrc !== 'NONE';
    const hasUpdatedPostalCodeLocations = postalCodeLocations.length;
    return hasApiConfigured && !hasUpdatedPostalCodeLocations;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    let navigateToServices = true;
    let locations: PostalCodeLocation[] = [];

    if (zipCodeRef?.current && !isZipCodeValid(zipCodeRef.current.value)) {
      setIsValidPostalCode(isZipCodeValid(zipCodeRef.current.value));
      navigateToServices = false;
    }

    if (shouldUpdatePostalCodeLocations()) {
      const result = await updatePostalCodeLocations();
      navigateToServices = result.navigateToServices;
      locations = result.locations;
    }

    if (navigateToServices) {
      saveUpdatedSessionItem('search', { postCode: searchPostCode });
      if (locations.length === 1 && locations[0].state) {
        dispatch(updateCountryStateInSearch(locations[0].state));
      }
      const path = getPath(appOperation, 'services', view);
      navigate(addPersistingQueryParamsToPath(path));
    }
  };

  useEffect((): void => {
    // Clearing stale postal code locations when postal code changes
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (postalCodeLocations.length) setPostalCodeLocations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPostCode]);

  return (
    <>
      <PageTitle title={t('pages.start.pageTitle')} />
      <form onSubmit={handleSubmit}>
        <StyledWrapper $withStickyFooter={isViewAlt || isViewApp}>
          <PageContent>
            <ContentWrapper>
              <Text tagName="h2" textStyle="Heading.L">
                {isViewAlt
                  ? t('pages.start.plainViewTitle')
                  : t('pages.start.title')}
              </Text>
              {showLoginLink && (
                <LoginLink
                  showPromptCard
                  showLoginInfoText={!!loginRequired.length}
                />
              )}
              {useInfoMessage.operations?.includes(appOperation) && (
                <StyledInlineMessage
                  data-cy="start-page-info-message"
                  ssrIcon={infoCircle}
                  body={t('pages.start.infoMessage')}
                  labelMessageType={t(
                    'generic.iconVariantAriaText.informative',
                  )}
                />
              )}
              <StyledText tagName="p" textStyle="Body.L">
                {isViewAlt
                  ? t('pages.start.plainViewDescription')
                  : t('pages.start.description')}
              </StyledText>

              <ZipCode
                name="zip"
                defaultValue={searchPostCode || userPostCode || ''}
                isValid={isValidPostalCode}
                isAltView={isViewAlt}
                setIsValid={setIsValidPostalCode}
                customRef={zipCodeRef}
                postalCodeLocations={postalCodeLocations}
              />
            </ContentWrapper>
            {isViewAlt || isViewApp ? (
              <StickyFooter>
                <Button
                  htmlType="submit"
                  id="nextButton"
                  disabled={!isValidPostalCode || !searchPostCode}
                  loading={isLoadingPostalCodeLocations}
                  text={t('components.stickyFooter.nextButton')}
                  type="emphasised"
                  aria-label={t('components.stickyFooter.ariaLabelToServices')}
                  data-cy="next-button"
                />
              </StickyFooter>
            ) : (
              <AppFooter
                isLoading={isLoadingPostalCodeLocations}
                nextVisible
                nextDisabled={!isValidPostalCode || !searchPostCode}
                nextAriaLabel={t('components.stickyFooter.ariaLabelToServices')}
              />
            )}
            {isFeatureEnabled('testTranslation') && (
              <Text tagName="p" textStyle="Body.L">
                {t('TEST_TRANSLATIONS')}
              </Text>
            )}
          </PageContent>
        </StyledWrapper>
      </form>
    </>
  );
};

export default StartPage;
